import { useState } from 'react';

import type { Practice } from '../api/types';
import styles from './LoopRing.module.css';

interface LoopRingProps {
  practices: Practice[];
  coreLabel: string;
  /** Shown in the core when no node is active. */
  defaultNote: string;
}

/** Node n of N, starting at the top and going clockwise. */
function positionFor(index: number, total: number): { x: number; y: number; above: boolean } {
  const angle = -Math.PI / 2 + (index * 2 * Math.PI) / total;
  const x = Math.cos(angle);
  const y = Math.sin(angle);
  return { x, y, above: y < -0.01 };
}

export function LoopRing({ practices, coreLabel, defaultNote }: LoopRingProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const active = practices.find((practice) => practice.id === activeId);
  const note = active ? active.note : defaultNote;

  return (
    <div className={styles.ring}>
      <div className={styles.orbit} aria-hidden="true" />
      <div className={styles.orbitInner} aria-hidden="true" />

      <div className={styles.spin} aria-hidden="true">
        <span className={styles.trace} />
      </div>

      {practices.map((practice, index) => {
        const { x, y, above } = positionFor(index, practices.length);
        // A top node's label sits outside the orbit, so it is pushed clear of
        // the ring; the lower ones sit inside and need no offset. Keep this in
        // step with `--ring-pad`, which is sized to just contain it.
        const offset = above ? 24 : 0;

        return (
          <a
            key={practice.id}
            href={`#${practice.id}`}
            className={`${styles.node} ${above ? styles.nodeAbove : ''}`}
            data-active={practice.id === activeId}
            style={{
              transform: `translate(calc(var(--ring-r) * ${x.toFixed(4)}), calc(var(--ring-r) * ${y.toFixed(4)} - ${offset}px))`,
              marginTop: above ? '-8px' : '-6px',
            }}
            onMouseEnter={() => setActiveId(practice.id)}
            onMouseLeave={() => setActiveId(null)}
            onFocus={() => setActiveId(practice.id)}
            onBlur={() => setActiveId(null)}
          >
            <span className={styles.dot} aria-hidden="true" />
            {practice.node}
          </a>
        );
      })}

      <div className={styles.core}>
        <span className={styles.coreLabel}>{coreLabel}</span>
        {/* Announced politely so a screen reader following the nodes hears the
            note change without it interrupting anything. */}
        <span className={styles.coreNote} aria-live="polite">
          {note}
        </span>
      </div>
    </div>
  );
}
